import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Use service layer instead of direct Firestore access
import { getFeature, getFeatureConfigurations, updateFeatureConfigurations } from '../../services/featureService';
import { getAllFacilityGroups, getAllFacilities } from '../../services/facilityService';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  Collapse,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import BusinessIcon from '@mui/icons-material/Business';

const FeatureConfiguration = () => {
  const { featureId } = useParams();
  const navigate = useNavigate();
  const [feature, setFeature] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchFeatureAndFacilities = useCallback(async () => {
    try {
      // Use the service layer instead of direct Firestore access
      const [featureData, groupsData, facilitiesData, existingConfigs] = await Promise.all([
        getFeature(featureId),
        getAllFacilityGroups(),
        getAllFacilities(),
        getFeatureConfigurations(featureId)
      ]);

      setFeature(featureData);

      // Process groups data
      const processedGroups = groupsData.map(group => ({
        ...group,
        facilities: Array.isArray(group.facilities) ? group.facilities : []
      }));
      setGroups(processedGroups);

      // Process facilities data with group information
      const processedFacilities = facilitiesData.map(facility => {
        const group = processedGroups.find(g => 
          Array.isArray(g.facilities) && 
          g.facilities.includes(facility.id)
        );
        return {
          ...facility,
          groupId: group?.id || null,
          groupName: group?.name || ''
        };
      });
      setFacilities(processedFacilities);

      setSelectedFacilities(existingConfigs);

      // Determine which groups should be selected based on existing configurations
      const selectedGroupIds = [];
      processedGroups.forEach(group => {
        if (group.facilities.length > 0 && 
            group.facilities.every(facilityId => existingConfigs.includes(facilityId))) {
          selectedGroupIds.push(group.id);
        }
      });
      setSelectedGroups(selectedGroupIds);

      // Expand all groups by default for better UX
      const expanded = {};
      processedGroups.forEach(group => {
        expanded[group.id] = true;
      });
      expanded['ungrouped'] = true;
      setExpandedGroups(expanded);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [featureId]);

  useEffect(() => {
    fetchFeatureAndFacilities();
  }, [fetchFeatureAndFacilities]);

  const getFacilitiesForGroup = (groupId) => {
    if (groupId === 'ungrouped') {
      return facilities.filter(facility => !facility.groupId);
    }
    const group = groups.find(g => g.id === groupId);
    if (!group) return [];
    return facilities.filter(facility => 
      group.facilities.includes(facility.id)
    );
  };

  const handleToggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleToggleGroupSelection = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    const isCurrentlySelected = selectedGroups.includes(groupId);
    
    if (isCurrentlySelected) {
      // Deselect group and all its facilities
      setSelectedGroups(prev => prev.filter(id => id !== groupId));
      setSelectedFacilities(prev => 
        prev.filter(facilityId => !group.facilities.includes(facilityId))
      );
    } else {
      // Select group and all its facilities
      setSelectedGroups(prev => [...prev, groupId]);
      setSelectedFacilities(prev => {
        const newSelected = [...prev];
        group.facilities.forEach(facilityId => {
          if (!newSelected.includes(facilityId)) {
            newSelected.push(facilityId);
          }
        });
        return newSelected;
      });
    }
  };

  const handleToggleFacility = (facilityId) => {
    const facility = facilities.find(f => f.id === facilityId);
    if (!facility) return;

    const isCurrentlySelected = selectedFacilities.includes(facilityId);
    
    if (isCurrentlySelected) {
      // Deselect facility
      setSelectedFacilities(prev => prev.filter(id => id !== facilityId));
      
      // If this facility was part of a selected group, deselect the group
      if (facility.groupId && selectedGroups.includes(facility.groupId)) {
        setSelectedGroups(prev => prev.filter(id => id !== facility.groupId));
      }
    } else {
      // Select facility
      setSelectedFacilities(prev => [...prev, facilityId]);
      
      // Check if all facilities in this group are now selected
      if (facility.groupId) {
        const group = groups.find(g => g.id === facility.groupId);
        if (group && group.facilities.every(fId => 
          selectedFacilities.includes(fId) || fId === facilityId
        )) {
          setSelectedGroups(prev => [...prev, facility.groupId]);
        }
      }
    }
  };

  const isGroupSelected = (groupId) => {
    return selectedGroups.includes(groupId);
  };

  const isGroupIndeterminate = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group || group.facilities.length === 0) return false;
    
    const selectedFacilitiesInGroup = group.facilities.filter(facilityId => 
      selectedFacilities.includes(facilityId)
    );
    
    return selectedFacilitiesInGroup.length > 0 && 
           selectedFacilitiesInGroup.length < group.facilities.length;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Use the service layer instead of direct Firestore access
      await updateFeatureConfigurations(featureId, selectedFacilities);
      console.log('Successfully saved feature configurations:', { featureId, selectedFacilities });
      
      navigate('/admin/features');
    } catch (error) {
      console.error('Error saving configurations:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!feature) {
    return <div>Feature not found</div>;
  }

  const ungroupedFacilities = getFacilitiesForGroup('ungrouped');

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/features')}
          style={{ marginRight: '1rem' }}
        >
          Back
        </Button>
        <Typography variant="h5">Configure {feature.name}</Typography>
      </div>

      <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
        <Typography variant="body1">
          <strong>Feature Key:</strong> {feature.key}
        </Typography>
        <Typography variant="body1">
          <strong>Description:</strong> {feature.description}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedFacilities.length > 0 && selectedFacilities.length < facilities.length}
                  checked={facilities.length > 0 && selectedFacilities.length === facilities.length}
                  onChange={() => {
                    if (selectedFacilities.length === facilities.length) {
                      setSelectedFacilities([]);
                      setSelectedGroups([]);
                    } else {
                      setSelectedFacilities(facilities.map(f => f.id));
                      setSelectedGroups(groups.map(g => g.id));
                    }
                  }}
                />
              </TableCell>
              <TableCell></TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Facility Groups & Facilities
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Location
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Type
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Render Facility Groups */}
            {groups.map((group) => {
              const groupFacilities = getFacilitiesForGroup(group.id);
              if (groupFacilities.length === 0) return null;

              return (
                <React.Fragment key={group.id}>
                  {/* Group Header Row */}
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isGroupSelected(group.id)}
                        indeterminate={isGroupIndeterminate(group.id)}
                        onChange={() => handleToggleGroupSelection(group.id)}
                      />
                    </TableCell>
                    <TableCell padding="none">
                      <IconButton
                        size="small"
                        onClick={() => handleToggleGroup(group.id)}
                      >
                        {expandedGroups[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {group.name} ({groupFacilities.length})
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{group.address || ''}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        Facility Group
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Group Facilities */}
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={expandedGroups[group.id]} timeout="auto" unmountOnExit>
                        <Table size="small">
                          <TableBody>
                            {groupFacilities.map((facility) => (
                              <TableRow key={facility.id}>
                                <TableCell padding="checkbox" sx={{ pl: 6 }}>
                                  <Checkbox
                                    checked={selectedFacilities.includes(facility.id)}
                                    onChange={() => handleToggleFacility(facility.id)}
                                    disabled={isGroupSelected(group.id)}
                                  />
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center">
                                    <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                                    {facility.name}
                                  </Box>
                                </TableCell>
                                <TableCell>{facility.location}</TableCell>
                                <TableCell>{facility.type}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {/* Ungrouped Facilities */}
            {ungroupedFacilities.length > 0 && (
              <React.Fragment>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={ungroupedFacilities.every(f => selectedFacilities.includes(f.id))}
                      indeterminate={
                        ungroupedFacilities.some(f => selectedFacilities.includes(f.id)) &&
                        !ungroupedFacilities.every(f => selectedFacilities.includes(f.id))
                      }
                      onChange={() => {
                        const allSelected = ungroupedFacilities.every(f => selectedFacilities.includes(f.id));
                        if (allSelected) {
                          setSelectedFacilities(prev => 
                            prev.filter(id => !ungroupedFacilities.map(f => f.id).includes(id))
                          );
                        } else {
                          setSelectedFacilities(prev => [
                            ...prev,
                            ...ungroupedFacilities.map(f => f.id).filter(id => !prev.includes(id))
                          ]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell padding="none">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleGroup('ungrouped')}
                    >
                      {expandedGroups['ungrouped'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <FolderIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        Unassigned Facilities ({ungroupedFacilities.length})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      No Group
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                    <Collapse in={expandedGroups['ungrouped']} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableBody>
                          {ungroupedFacilities.map((facility) => (
                            <TableRow key={facility.id}>
                              <TableCell padding="checkbox" sx={{ pl: 6 }}>
                                <Checkbox
                                  checked={selectedFacilities.includes(facility.id)}
                                  onChange={() => handleToggleFacility(facility.id)}
                                />
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                                  {facility.name}
                                </Box>
                              </TableCell>
                              <TableCell>{facility.location}</TableCell>
                              <TableCell>{facility.type}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default FeatureConfiguration; 